
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { CreditCard, Phone, Instagram, Facebook, MessageCircle, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface PaymentInformationSectionProps {
  useGiftCard?: boolean;
  useSubscription?: string; // "pay_individually" or subscription id
  forSubscriptions?: boolean; // Whether this is for the subscriptions section
}

export const PaymentInformationSection: React.FC<PaymentInformationSectionProps> = ({ 
  useGiftCard,
  useSubscription,
  forSubscriptions = false
}) => {
  const { t } = useTranslation();
  
  // Hide payment information if using gift card or subscription (not set to pay_individually)
  // but always show it if rendering in the subscriptions context with forSubscriptions=true
  if (!forSubscriptions && (useGiftCard || (useSubscription && useSubscription !== 'pay_individually'))) {
    return null;
  }

  const handleCopy = (text: string, itemName: string, e: React.MouseEvent) => {
    // Prevent default to avoid form submission
    e.preventDefault();
    e.stopPropagation();
    
    navigator.clipboard.writeText(text)
      .then(() => {
        toast.success(t('booking.payment.copied', `${itemName} copied to clipboard`));
      })
      .catch(() => {
        toast.error(t('booking.payment.copyFailed', `Failed to copy ${itemName}`));
      });
  };
  
  return (
    <div className="space-y-4">
      <Alert className="bg-primary/10 border-primary/20">
        <AlertTitle className="text-primary font-medium">
          {forSubscriptions 
            ? t('dashboard.subscriptions.pendingPaymentTitle', 'Complete Your Payment') 
            : t('booking.payment.title', 'Payment Information')}
        </AlertTitle>
        <AlertDescription className="text-muted-foreground">
          {forSubscriptions 
            ? t('dashboard.subscriptions.pendingPaymentDesc', 'You have pending subscription payments. Please use one of the methods below to complete payment:') 
            : t('booking.payment.description', 'Payment for the class must be made in advance.')}
        </AlertDescription>
      </Alert>
      
      <Card className="border-primary/20">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary">
              <CreditCard size={18} />
            </div>
            <div className="space-y-0.5 flex-1">
              <h4 className="font-medium text-sm">{t('booking.payment.bankDetails', 'Bank Details')}</h4>
              <div className="grid grid-cols-1 gap-1 text-sm text-muted-foreground">
                <div className="flex flex-col sm:flex-row sm:gap-2 sm:items-center">
                  <span className="font-medium">{t('booking.payment.cardNumber', 'Card Number')}:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">4083 0900 1014 7679</span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-6 w-6 rounded-full"
                      onClick={(e) => handleCopy("4083 0900 1014 7679", t('booking.payment.cardNumber', 'Card Number'), e)}
                      title={t('booking.payment.copyCardNumber', 'Copy card number')}
                      type="button" // Explicitly set type to button to prevent form submission
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:gap-2 sm:items-center">
                  <span className="font-medium">{t('booking.payment.cardHolder', 'Card Holder')}:</span>
                  <div className="flex items-center gap-2">
                    <span>ERNEST VOSKANYAN</span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-6 w-6 rounded-full"
                      onClick={(e) => handleCopy("ERNEST VOSKANYAN", t('booking.payment.cardHolder', 'Card Holder'), e)}
                      title={t('booking.payment.copyCardHolder', 'Copy card holder name')}
                      type="button" // Explicitly set type to button to prevent form submission
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:gap-2">
                  <span className="font-medium">{t('booking.payment.bank', 'Bank')}:</span>
                  <span>Ameria bank</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-start gap-3 pt-2">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary mt-1">
              <Phone size={18} />
            </div>
            <div>
              <h4 className="font-medium text-sm">{t('booking.payment.instructions', 'Payment Instructions')}</h4>
              <p className="text-sm text-muted-foreground mt-1">
                {t('booking.payment.sendDetails', 'Please send the payment receipt via one of these platforms and include your phone number for identification:')}
              </p>
              <div className="flex flex-wrap gap-3 mt-3">
                <a 
                  href="https://www.instagram.com/f_ata_to/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm bg-pink-50 text-pink-600 hover:bg-pink-100 px-3 py-1.5 rounded-md transition-colors"
                >
                  <Instagram size={16} />
                  <span>Instagram</span>
                </a>
                <a 
                  href="https://www.facebook.com/fatatoam/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors"
                >
                  <Facebook size={16} />
                  <span>Facebook</span>
                </a>
                <a 
                  href="https://wa.me/37493049779" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm bg-green-50 text-green-600 hover:bg-green-100 px-3 py-1.5 rounded-md transition-colors"
                >
                  <MessageCircle size={16} />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
